import { MouseEvent, useState } from 'react'

import IconButton from '@components/IconButton/IconButton'
import { faCalendarRange } from '@fortawesome/pro-light-svg-icons'
import { Menu, MenuItem } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'

type DateRange = {
	label: string
	start: Dayjs
	end: Dayjs
}

type Props = {
	onClick: (value: { start: Dayjs; end: Dayjs } | null) => void
	ranges?: DateRange[]
}

const defaultRanges = [
	{
		label: 'This Week',
		start: dayjs().startOf('week'),
		end: dayjs().endOf('week'),
	},
	{
		label: 'This Month',
		start: dayjs().startOf('month'),
		end: dayjs().endOf('month'),
	},
	{
		label: 'This Year',
		start: dayjs().startOf('year'),
		end: dayjs().endOf('year'),
	},
	{
		label: 'Year to Date',
		start: dayjs().startOf('year'),
		end: dayjs(),
	},
	{
		label: 'Last Week',
		start: dayjs().startOf('week').subtract(1, 'week'),
		end: dayjs().startOf('week').subtract(1, 'day'),
	},
	{
		label: 'Last Month',
		start: dayjs().startOf('month').subtract(1, 'month'),
		end: dayjs().startOf('month').subtract(1, 'day'),
	},
	{
		label: 'Last Quarter',
		start: dayjs().startOf('quarter').subtract(1, 'quarter'),
		end: dayjs().startOf('quarter').subtract(1, 'day'),
	},
]

export default function DateRangeMenu({ onClick, ranges = defaultRanges, ...rest }: Props) {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
	const open = Boolean(anchorEl)

	const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation()
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const handleItemClick = (start: Dayjs, end: Dayjs) => {
		handleClose()
		onClick({ start, end })
	}

	return (
		<>
			<IconButton
				id="date-selector-button"
				aria-controls={open ? 'date-menu' : undefined}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : undefined}
				onClick={handleClick}
				size="small"
				{...rest}
				icon={faCalendarRange}
			/>
			<Menu
				id="date-selector-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				MenuListProps={{
					'aria-labelledby': 'date-selector-button',
				}}
			>
				{ranges.map(({ label, start, end }) => (
					<MenuItem
						key={label}
						onClick={(e) => {
							e.stopPropagation()
							handleItemClick(start, end)
						}}
					>
						{label}
					</MenuItem>
				))}
			</Menu>
		</>
	)
}
